import React, { useContext, useEffect, useReducer, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';

//Helpers
import {
    buildBooleanOptions,
    getDaysInMonth,
    isEqualArray,
    formatNumber,
    imageFileToBase64,
} from '../helpers';

//Servicios
import {
    BodegasService,
    ConstantsService,
    SettingsService,
    UserWaitingHabilitationService,
    ViniedoService,
} from '../services/api';

//Componentes
import Spinner from '../components/spinner/Spinner';

//Contexto
const GlobalContext = React.createContext();

export function useGlobalContext() {
    return useContext(GlobalContext);
}

const InitialSearchParameters = {
    declaraciones: {},
    viniedos: {},
    guias: {},
    usuarios: {},
};

const SearchParametersReducer = (state, action) => {
    switch (action.type) {
        case 'DECLARACIONES':
            return { ...state, declaraciones: action.payload };
        case 'VINIEDOS':
            return { ...state, viniedos: action.payload };
        case 'GUIAS':
            return { ...state, guias: action.payload };
        case 'USUARIOS':
            return { ...state, usuarios: action.payload };
        case 'LOGOUT':
            return (state = InitialSearchParameters);
        default:
            return state;
    }
};

function GlobalProvider({ children }) {
    const [loader, setLoader] = useState(false);
    const [settings, setSettings] = useState({});
    const [tablePage, setTablePage] = useState(1);
    const [masters, setMasters] = useState({});
    const [token, setToken] = useState(window.sessionStorage.getItem('token'));
    const [user, setUser] = useState(
        JSON.parse(window.sessionStorage.getItem('user'))
    );
    const [usersForHabilitation, setUsersForHabilitation] = useState([]);
    const [viniedos, setViniedos] = useState([]);
    const [grapesVariety, setGrapesVariety] = useState([]);
    const [Bodegas, setBodegas] = useState([]);
    const [SearchParameters, dispatchSearchParameters] = useReducer(
        SearchParametersReducer,
        InitialSearchParameters
    );

    function logOut() {
        window.sessionStorage.clear();
        setToken(null);
        setUser(null);
        dispatchSearchParameters({
            type: 'LOGOUT',
        });
    }

    const days = ['Do', 'Lu', 'Ma', 'Mie', 'Ju', 'Vi', 'Sa'];
    const months = [
        'Enero',
        'Febrero',
        'Marzo',
        'Abril',
        'Mayo',
        'Junio',
        'Julio',
        'Agosto',
        'Setiembre',
        'Octubre',
        'Noviembre',
        'Diciembre',
    ];
    const localeDateFormat = {
        localize: {
            day: (n) => days[n],
            month: (n) => months[n],
        },
        formatLong: {
            date: () => 'dd/MM/yyyy',
        },
    };

    const url = useLocation();

    useEffect(() => setTablePage(1), [url.pathname]);

    useEffect(() => {
        (async function () {
            setLoader(true);
            await fetchMasters();
            if (token && user.tipo === 3) await fetchUsersWaitingHabilitation();
            if (token && user.tipo !== 3) await fetchViniedos();
            if (token) {
                await fetchSetting();
                await fetchGrapesVariety();
                await fetchBodegas();
            }
            setLoader(false);
        })();
    }, []);

    async function fetchMasters() {
        try {
            let allMasters = {};
            let mastersNames = [
                'UserTypes',
                'DocumentTypes',
                'HabilitationTypes',
                'GuiaStateTypes',
                'AccionTypes',
                'ObjectTypes',
                'LetterTypes',
                'Departments',
                'Destinos',
            ];

            let masters = await Promise.all([
                await ConstantsService.userTypes().catch((err) => {
                    return err;
                }),
                await ConstantsService.documentTypes().catch((err) => {
                    return err;
                }),
                await ConstantsService.habilitationTypes().catch((err) => {
                    return err;
                }),
                await ConstantsService.guiaStateTypes().catch((err) => {
                    return err;
                }),
                await ConstantsService.accionTypes().catch((err) => {
                    return err;
                }),
                await ConstantsService.objectTypes().catch((err) => {
                    return err;
                }),
                await ConstantsService.letterTypes().catch((err) => {
                    return err;
                }),
                await ConstantsService.departments().catch((err) => {
                    return err;
                }),
                await ConstantsService.destinos().catch((err) => {
                    return err;
                }),
            ]);

            masters.forEach((master, index) => {
                allMasters[mastersNames[index]] = master.data;
            });

            setMasters(allMasters);
        } catch (err) {
            return err;
        }
    }

    async function fetchUsersWaitingHabilitation() {
        try {
            const usersWaitingHabilitation =
                await UserWaitingHabilitationService.all({ token });

            if (usersWaitingHabilitation) {
                setUsersForHabilitation(usersWaitingHabilitation.data);
            }
        } catch (err) {
            return err;
        }
    }

    async function fetchSetting() {
        try {
            const settingsData = await SettingsService.all({ token });

            if (settingsData) {
                setSettings(settingsData.data);
            }
        } catch (err) {
            return err;
        }
    }

    async function fetchViniedos() {
        try {
            const viniedos = await ViniedoService.all({
                token: token,
                usuarioId: user.id,
            });

            if (viniedos) {
                setViniedos(viniedos.data);
            }
        } catch (err) {
            return err;
        }
    }

    async function fetchGrapesVariety() {
        try {
            const grapesVarietyData = await ViniedoService.grapesVariety({
                token,
            });

            if (grapesVarietyData) {
                setGrapesVariety(grapesVarietyData.data);
            }
        } catch (err) {
            return err;
        }
    }

    async function fetchBodegas() {
        try {
            const Bodegas = await BodegasService.all({
                token,
            });

            if (Bodegas) {
                setBodegas(Bodegas.data);
            }
        } catch (err) {
            return err;
        }
    }

    function buildSelectOptions(master) {
        let m;
        if (Object.keys(masters).includes(master) && masters[master].length) {
            m = masters[master].map((m) => {
                return {
                    title: m.nombre.replaceAll('_', ' '),
                    name: `${m.id}`,
                    value: m.id,
                };
            });
        }
        return m;
    }

    return loader ? (
        <Spinner />
    ) : (
        <GlobalContext.Provider
            value={{
                toast,
                settings,
                masters,
                token,
                user,
                usersForHabilitation,
                viniedos,
                tablePage,
                grapesVariety,
                Bodegas,
                SearchParameters,
                days,
                months,
                localeDateFormat,
                logOut,
                setSettings,
                setMasters,
                setToken,
                setUser,
                setUsersForHabilitation,
                setViniedos,
                setTablePage,
                setGrapesVariety,
                setBodegas,
                dispatchSearchParameters,
                buildSelectOptions,
                buildBooleanOptions,
                getDaysInMonth,
                isEqualArray,
                formatNumber,
                imageFileToBase64,
                fetchSetting,
            }}
        >
            {children}
        </GlobalContext.Provider>
    );
}

export default GlobalProvider;
